import { useRef, useCallback, useEffect } from 'react';
import { logShowImpression } from '../util/events';

const FLUSH_INTERVAL_MS = 5000;
const VISIBILITY_THRESHOLD = 0.5;
const DWELL_MS = 500;

const useImpressionTracking = source => {
  const cardNodesRef = useRef(new Map());
  const nodeToShowIdRef = useRef(new Map());
  const pendingShowIdsRef = useRef(new Set());
  const sentShowIdsRef = useRef(new Set());
  const refCallbacksRef = useRef(new Map());
  const dwellTimersRef = useRef(new Map());

  const clearDwellTimer = useCallback(showId => {
    const timer = dwellTimersRef.current.get(showId);
    if (timer) {
      clearTimeout(timer);
      dwellTimersRef.current.delete(showId);
    }
  }, []);

  const observerRef = useRef(null);
  if (observerRef.current === null && typeof IntersectionObserver !== 'undefined') {
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const showId = nodeToShowIdRef.current.get(entry.target);
          if (showId == null) return;

          if (!entry.isIntersecting) {
            clearDwellTimer(showId);
            return;
          }

          if (sentShowIdsRef.current.has(showId) || dwellTimersRef.current.has(showId)) return;

          const timer = setTimeout(() => {
            dwellTimersRef.current.delete(showId);
            pendingShowIdsRef.current.add(showId);
          }, DWELL_MS);
          dwellTimersRef.current.set(showId, timer);
        });
      },
      { threshold: VISIBILITY_THRESHOLD }
    );
  }

  useEffect(() => {
    const observer = observerRef.current;
    const dwellTimers = dwellTimersRef.current;

    const flush = async () => {
      const ids = Array.from(pendingShowIdsRef.current);
      if (ids.length === 0) return;
      pendingShowIdsRef.current.clear();
      ids.forEach(id => sentShowIdsRef.current.add(id));
      const success = await logShowImpression(ids, source);
      if (!success) {
        ids.forEach(id => {
          sentShowIdsRef.current.delete(id);
          pendingShowIdsRef.current.add(id);
        });
      }
    };

    const interval = setInterval(flush, FLUSH_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      void flush();
      observer?.disconnect();
      dwellTimers.forEach(timer => clearTimeout(timer));
      dwellTimers.clear();
    };
  }, [source]);

  return useCallback(showId => {
    if (!refCallbacksRef.current.has(showId)) {
      refCallbacksRef.current.set(showId, node => {
        const observer = observerRef.current;
        const prevNode = cardNodesRef.current.get(showId);
        if (prevNode && prevNode !== node) {
          observer?.unobserve(prevNode);
          nodeToShowIdRef.current.delete(prevNode);
        }
        if (node) {
          cardNodesRef.current.set(showId, node);
          nodeToShowIdRef.current.set(node, showId);
          observer?.observe(node);
        } else {
          cardNodesRef.current.delete(showId);
          refCallbacksRef.current.delete(showId);
          clearDwellTimer(showId);
        }
      });
    }
    return refCallbacksRef.current.get(showId);
  }, [clearDwellTimer]);
};

export default useImpressionTracking;
